import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, signal } from '@angular/core';
import { WhStateStore } from 'src/app/shared/store/state.store';
import { environment } from 'src/environments/environment';

/**
 * Utility: schedule work strictly after first paint, safe from NG0100.
 * - Uses Angular's afterNextRender if available.
 * - Falls back to rAF + microtask.
 * - No-op on SSR.
 */
function scheduleAfterFirstPaint(task: () => void) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    afterNextRender(() => task());
    return;
  } catch {
    // ignore and fallback
  }
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => queueMicrotask(task));
  } else {
    setTimeout(task, 0);
  }
}

@Component({
  selector: 'wh-debugger',
  imports: [],
  templateUrl: './debugger.html',
  styleUrl: './debugger.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.invisible]': '!visible()',
  },
})
export class Debugger {
  // --- DI --------------------------------------------------------------------
  private readonly stateStore = inject(WhStateStore);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  // --- Safe-area signals -----------------------------------------------------
  readonly top = signal(0);
  readonly right = signal(0);
  readonly bottom = signal(0);
  readonly left = signal(0);

  private readonly isPwa = this.stateStore.isPwa;
  private readonly ready = signal(false);

  readonly visible = computed(() => environment.showPwaFrameDebugger && this.isPwa() && this.ready());

  private readonly onResize = () => this.readSafeAreas();

  // --- Effect: keep <html> class in sync with visible() ----------------------
  private readonly syncHtmlClass = effect(() => {
    if (!this.ready()) return;
    const el = document.documentElement;
    if (this.visible()) el.classList.add('debug-notch');
    else el.classList.remove('debug-notch');
  });

  ngOnInit(): void {
    scheduleAfterFirstPaint(() => {
      // 1) Decide if we emulate (dev + PWA) WITHOUT waiting for `ready()`
      const emulate = environment.showPwaFrameDebugger && this.isPwa();

      // 2) Apply the class BEFORE reading CSS variables
      if (emulate) document.documentElement.classList.add('debug-notch');

      // 3) Now read the values (they’ll reflect the .debug-notch overrides)
      this.readSafeAreas();

      // 4) Start listeners after paint
      window.addEventListener('resize', this.onResize, { passive: true });

      // 5) Reveal overlay last (prevents NG0100)
      this.ready.set(true);
    });
  }


  ngOnDestroy(): void {
    document.documentElement.classList.remove('debug-notch');
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
    }
  }

  // --- Internal --------------------------------------------------------------
  private readSafeAreas(): void {
    if (typeof document === 'undefined') return; // SSR guard
    const cs = getComputedStyle(document.documentElement);
    this.top.set(parseInt(cs.getPropertyValue('--safe-top')) || 0);
    this.right.set(parseInt(cs.getPropertyValue('--safe-right')) || 0);
    this.bottom.set(parseInt(cs.getPropertyValue('--safe-bottom')) || 0);
    this.left.set(parseInt(cs.getPropertyValue('--safe-left')) || 0);
  }
}
